import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PartnerContractResponse } from '@interfaces/partners/contracts/responses/partner-contract.response';
import { TranslateService } from '@ngx-translate/core';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnerContractsHttpService } from '@services/http/partners/partner-contracts-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { PartnerContractPartyFormComponent } from '../../../partner-contract-party-form/partner-contract-party-form.component';
import {
  createPartnerContractPartyForm,
  partnerContractPartyFormValue,
  readPartnerContractParty,
} from '../../../partner-contract-party-form/partner-contract-party-form';
import { backendErrorMessage } from '../../../../utils/partner-contract.utils';

/** Dados do parceiro que preenchem os padrões da modal. */
export interface GeneratePartnerContractContext {
  partnerId: string;
  partnerName: string;
  partnerEmail: string;
}

/**
 * Gera a minuta do Contrato de Parceria Comercial a partir do modelo vigente (card 3462).
 *
 * Pede os dados da parte que preenchem as variáveis do texto. Só razão social e data são
 * obrigatórias: o que ficar vazio continua no texto como `{{chave}}` e pode ser preenchido depois,
 * no editor. Ao gerar, emite a minuta criada; quem abriu a modal navega para o editor.
 */
@Component({
  selector: 'app-modal-generate-partner-contract',
  standalone: true,
  templateUrl: './modal-generate-partner-contract.component.html',
  styleUrls: ['./modal-generate-partner-contract.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, SharedModule, PartnerContractPartyFormComponent],
})
export class ModalGeneratePartnerContractComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** Minuta criada; o pai navega para o editor. */
  @Output() generated = new EventEmitter<PartnerContractResponse>();

  /**
   * Gravando. Nome próprio porque `isLoading` do `VaultBaseComponent` é o spinner global (NgRx).
   */
  saving = false;
  readonly form: FormGroup;

  private partnerId = '';

  constructor(
    translateService: TranslateService,
    private readonly fb: FormBuilder,
    private readonly contractsService: PartnerContractsHttpService,
    private readonly dateFormatter: DateFormatterService,
    private readonly toast: ToastHelperService,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(translateService);
    this.form = createPartnerContractPartyForm(this.fb);
  }

  /** Abre a modal com os padrões do parceiro (nome curto = nome; e-mail do representante = e-mail). */
  open(context: GeneratePartnerContractContext): void {
    this.partnerId = context.partnerId;
    this.form.reset(
      partnerContractPartyFormValue({
        partyShortName: context.partnerName,
        partyRepresentativeEmail: context.partnerEmail,
        contractDate: this.dateFormatter.formatDateToYYYYMMDD(this.dateFormatter.getTodayDate()),
      })
    );
    this.saving = false;
    this.cdr.markForCheck();
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'generate-partner-contract', false, true);
  }

  isFormValid(): boolean {
    return this.form.valid && !!this.partnerId;
  }

  submitAction(): void {
    if (!this.isFormValid() || this.saving) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.cdr.markForCheck();

    this.contractsService
      .createContract({
        partnerId: this.partnerId,
        partyType: this.form.getRawValue().partyType,
        ...readPartnerContractParty(this.form, this.dateFormatter),
      })
      .subscribe({
        next: contract => {
          this.saving = false;
          this.cdr.markForCheck();
          if (!contract) return;
          this.toast.showToastSuccess(
            'Contrato gerado',
            contract.unfilledVariables?.length
              ? 'Alguns dados ficaram em branco. Preencha no painel "Dados da parte" do editor.'
              : 'O texto já saiu com os dados da parceira. Revise e ajuste no editor.'
          );
          this.dialogRef?.close();
          this.generated.emit(contract);
        },
        error: error => {
          this.saving = false;
          this.cdr.markForCheck();
          this.toast.showToastError(
            'Não foi possível gerar o contrato',
            backendErrorMessage(error) || 'Confira os dados e tente de novo.'
          );
        },
      });
  }
}
